import { useState } from "react";
import { Loader2, Plus } from "lucide-react";
import { toast } from "sonner";
import { trpc } from "@/lib/trpc";
import { useConfirm } from "@/lib/confirm";
import { AdicionarEspecialidadeDialog } from "./AdicionarEspecialidadeDialog";
import { ProcedimentosDaEspecialidadeDialog } from "./ProcedimentosDaEspecialidadeDialog";
import { CartaoDaEspecialidade } from "./CartaoDaEspecialidade";

/**
 * Especialidades da clínica em cartões: quantos procedimentos a clínica tem de cada uma; o cartão abre
 * a lista, com "Editar". Adicionar especialidade já leva aos procedimentos dela, marcados.
 */
export function EspecialidadesDaClinica({ podeEditar }: { podeEditar: boolean }) {
  const utils = trpc.useUtils();
  const confirmar = useConfirm();
  const minhas = trpc.clinicaOnboarding.minhas.useQuery(undefined, { enabled: podeEditar });
  const [adicionando, setAdicionando] = useState(false);
  const [procedimentosDe, setProcedimentosDe] = useState<{ id: string; label: string; novos: boolean } | null>(null);

  const invalidar = () => {
    utils.clinicaOnboarding.invalidate();
    utils.organization.activeSpecialties.invalidate();
    utils.settings.getAgenda.invalidate();
  };
  const adicionar = trpc.organization.addSpecialty.useMutation({
    onSuccess: (_r, v) => {
      invalidar();
      setAdicionando(false);
      const e = (utils.specialty.list.getData() ?? []).find((x) => x.id === v.specialtyId);
      if (e) setProcedimentosDe({ id: e.id, label: e.label, novos: true });
    },
    onError: (e) => toast.error(e.message || "Erro ao adicionar especialidade."),
  });
  const remover = trpc.organization.removeSpecialty.useMutation({
    onSuccess: () => {
      invalidar();
      toast.success("Especialidade removida. Os procedimentos dela continuam na clínica.");
    },
    onError: (e) => toast.error(e.message || "Erro ao remover especialidade."),
  });

  const removerComConfirmacao = async (e: { id: string; label: string }) => {
    const ok = await confirmar({
      title: `Remover ${e.label}?`,
      description: "Sai da lista de especialidades da clínica. Nenhum procedimento é apagado.",
      confirmText: "Remover",
    });
    if (ok) remover.mutate({ specialtyId: e.id });
  };

  const lista = minhas.data ?? [];
  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h3 className="text-sm font-semibold text-zinc-900 dark:text-foreground">Especialidades da clínica</h3>
          <p className="mt-1 text-[12.5px] text-zinc-500 dark:text-muted-foreground">
            Cada especialidade traz os procedimentos dela da base da plataforma — você escolhe quais a clínica faz.
          </p>
        </div>
      </div>

      {minhas.isLoading ? (
        <div className="flex justify-center py-8">
          <Loader2 className="h-5 w-5 animate-spin text-brand-600" />
        </div>
      ) : lista.length === 0 && !podeEditar ? (
        <p className="rounded-lg border border-dashed border-zinc-200 px-4 py-6 text-center text-sm text-zinc-500 dark:border-border dark:text-muted-foreground">
          Nenhuma especialidade ainda.
        </p>
      ) : (
        <div className="grid gap-3 sm:grid-cols-2">
          {lista.map((e) => (
            <CartaoDaEspecialidade
              key={e.id}
              especialidade={e}
              podeEditar={podeEditar}
              onEditar={() => setProcedimentosDe({ id: e.id, label: e.label, novos: false })}
              onRemover={() => removerComConfirmacao(e)}
            />
          ))}
          {/* Adicionar é mais um cartão da coleção, onde a especialidade nova vai aparecer. */}
          {podeEditar && (
            <button
              type="button"
              onClick={() => setAdicionando(true)}
              className="flex min-h-[64px] items-center justify-center gap-2 self-start rounded-lg border border-dashed border-zinc-300 px-3.5 py-3 text-sm font-semibold text-zinc-600 transition-colors hover:border-brand-400 hover:bg-brand-50/50 hover:text-brand-700 dark:border-border dark:text-muted-foreground dark:hover:bg-primary/5 dark:hover:text-primary"
            >
              <Plus className="h-4 w-4" />
              Adicionar especialidade
            </button>
          )}
        </div>
      )}

      <AdicionarEspecialidadeDialog
        open={adicionando}
        ativas={lista.map((e) => e.id)}
        ocupado={adicionar.isPending}
        onClose={() => setAdicionando(false)}
        onEscolher={(e) => adicionar.mutate({ specialtyId: e.id })}
      />
      <ProcedimentosDaEspecialidadeDialog especialidade={procedimentosDe} novos={procedimentosDe?.novos} onClose={() => setProcedimentosDe(null)} />
    </div>
  );
}
